import { useQuery } from '@tanstack/react-query';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { paths } from '@/app/paths';
import OrgMark from '@/components/OrgMark';
import { cn } from '@/design-system/lib/cn';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/design-system/ui/Menu';
import { orgPlace, orgUrl } from '@escala/domain/orgs';
import { useSwitchOrg } from '@/hooks/useSwitchOrg';
import { useStore } from '@/store/AppStore';
import { ORGS_KEY } from '@/store/orgs';

/**
 * A organização aberta, logo abaixo da marca. Para o Administrador da plataforma é um
 * menu com as organizações e "Gerenciar organizações"; para os demais, só informa.
 */
export default function OrgSwitcher({ labels, onNavigate }: { labels: boolean; onNavigate: () => void }) {
  const { org, orgs, permissions } = useStore();
  const navigate = useNavigate();
  const switchOrg = useSwitchOrg();
  const list = useQuery({ queryKey: ORGS_KEY, queryFn: orgs.list, enabled: permissions.manageOrgs });
  const box = cn(
    'flex w-full items-center gap-2.5 rounded-block border border-sidebar-card-line bg-sidebar-card p-2 text-left',
    !labels && 'justify-center'
  );
  const text = (sub: string) =>
    labels ? (
      <span className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="truncate text-control font-semibold text-white">{org.name}</span>
        <span className="truncate text-caption text-sidebar-muted">{sub}</span>
      </span>
    ) : null;

  if (!permissions.manageOrgs) {
    return (
      <div className="shrink-0 px-2.5 pb-2">
        <div className={box} title={org.name}>
          <OrgMark org={org} />
          {text(orgPlace(org) || orgUrl(org.slug))}
        </div>
      </div>
    );
  }

  return (
    <div className="shrink-0 px-2.5 pb-2">
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={`Organização atual: ${org.name}. Trocar organização`}
          title={org.name}
          className={cn(box, 'hover:border-sidebar-line hover:bg-sidebar-hover')}
        >
          <OrgMark org={org} />
          {text(orgUrl(org.slug))}
          {labels ? <ChevronDown aria-hidden className="size-4 shrink-0 text-sidebar-muted" /> : null}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-90 w-70 overflow-y-auto">
          <DropdownMenuLabel>Organizações</DropdownMenuLabel>
          {(list.data ?? []).map((o) => (
            <DropdownMenuItem
              key={o.id}
              onSelect={() => {
                onNavigate();
                if (o.id !== org.id) switchOrg(o.id);
              }}
            >
              <OrgMark org={o} size="sm" />
              <span className="flex min-w-0 flex-1 flex-col leading-tight">
                <span className="truncate font-semibold">{o.name}</span>
                <span className="truncate text-caption text-ink-subtle">
                  {o.status === 'suspended' ? 'Suspensa' : orgUrl(o.slug)}
                </span>
              </span>
              {o.id === org.id ? (
                <>
                  <Check aria-hidden className="text-primary" />
                  <span className="sr-only">(atual)</span>
                </>
              ) : null}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => {
              onNavigate();
              navigate(paths.orgs);
            }}
            className="font-semibold text-primary-hover"
          >
            <Building2 aria-hidden />
            Gerenciar organizações
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
